/**
 * The daily round's bricks and its payoff, "Build what I earned" (v1 ticket 15, variant D), without the DOM.
 *
 * A full-screen review card that is passed banks a brick, labelled with the word and the material the server
 * saved it at ("word → material"); a miss banks nothing. The server moves the box on save as always: the bank
 * only holds back the town's picture of it until the payoff, which builds the climbed buildings one at a time up
 * to `END_BUILD_CAP`, then the rest together. A round left without the payoff still changed the town.
 * Materials only; a box never shows.
 */
import { MATERIALS, type Material, type OwnTown, type OwnTownBuilding } from './own-town';

/**
 * Buildings the payoff flies to one at a time; the rest go up together in one step ("and N more"). Three keeps
 * the build near ten seconds (the arrival prototype's variant D).
 */
export const END_BUILD_CAP = 3;

export interface Brick {
  key: string;
  text: string;
  /** The material the pass was saved at. */
  material: Material;
  /** The pass kept the material (the word was not due, or the card was below its step). */
  kept: boolean;
}

/** The material a box shows as; a box itself never reaches the screen. */
export const materialOf = (box: number): Material => MATERIALS[Math.max(0, Math.min(4, Math.round(box)))];

/** What a saved answer was: the card's place in the round, and the answer as sent and graded. */
export interface SavedAnswer {
  firstLook?: boolean;
  spelling?: boolean;
  correct: boolean;
  revealed?: boolean;
  skipped?: boolean;
  selfReport?: boolean;
  /** The server's verdict on a typed answer, which replaces the client's. */
  graded?: { kind: 'right' | 'scratched' | 'wrong' };
}

/** Did the answer pass? The server's grade on typed text wins; a scratch passes. */
export const passed = (a: SavedAnswer) => (a.graded ? a.graded.kind !== 'wrong' : a.correct);

/**
 * Does this answer bank a brick? A scored pass on a review card: never a first look (it lifts its fog in the
 * town there and then), a spelling card (unscored), a reveal, a skip or an "I said it".
 */
export function banks(a: SavedAnswer): boolean {
  if (a.firstLook || a.spelling || a.revealed || a.skipped || a.selfReport) return false;
  return passed(a);
}

/**
 * Does an answer in a moment's first lesson in the town (v1 ticket 41) bank a brick? Only a climb, as the server
 * saved it (the reply's `climbed`): "Build what I earned" there builds only what the learner made climb. So never a
 * flip, a reveal, a skip or an "I said it", never a pass that kept the word where it was (not due, hinted, a card
 * below its step, an easier card), and never a word leaving the fog, which shows as its fog lifting instead.
 */
export function lessonBanks(a: SavedAnswer, r: { climbed?: true }): boolean {
  return !!r.climbed && banks(a);
}

/** The brick for a banked pass; `was` is the material the town showed before the round. */
export function brickOf(key: string, text: string, box: number, was: Material | undefined): Brick {
  const material = materialOf(box);
  return { key, text, material, kept: was === material };
}

/** Adds a brick to the bank; a word passed twice keeps one brick, at its latest material. */
export function bank(bricks: readonly Brick[], b: Brick): Brick[] {
  const i = bricks.findIndex((x) => x.key === b.key);
  if (i < 0) return [...bricks, b];
  const was = bricks[i];
  return bricks.map((x, k) => (k === i ? { ...b, kept: was.kept && b.kept } : x));
}

const rank = (m: Material) => MATERIALS.indexOf(m);

/** A lamp lit again today: off (or going out) before the round, lit and steady after it. */
export function relit(before: OwnTownBuilding | undefined, after: OwnTownBuilding | undefined): boolean {
  return !!after?.lamp && !after.fading && (!before?.lamp || !!before.fading);
}

/** The payoff's steps: the climbed buildings, first `END_BUILD_CAP` one at a time, then the rest together. */
export interface BuildPlan {
  one: string[];
  rest: string[];
  /** Banked words whose building changed without climbing (ivy gone, a lamp lit): they change with the rest. */
  touched: string[];
  /** Words among them whose lamp was lit again today, to flare. */
  relit: Set<string>;
}

/** Plans the payoff from the town on screen (`shown`) and the town after the round, for the bricks banked. */
export function buildPlan(bricks: readonly Brick[], shown: OwnTown, after: OwnTown, cap = END_BUILD_CAP): BuildPlan {
  const was = new Map(shown.buildings.map((b) => [b.key, b]));
  const now = new Map(after.buildings.map((b) => [b.key, b]));
  const climbed: string[] = [], touched: string[] = [], lit = new Set<string>();
  for (const { key } of bricks) {
    const a = was.get(key), b = now.get(key);
    if (!a || !b) continue;
    if (relit(a, b)) lit.add(key);
    if (rank(b.material) > rank(a.material)) climbed.push(key);
    else if (b.material !== a.material || a.ivy !== b.ivy || a.lamp !== b.lamp || !!a.fading !== !!b.fading) touched.push(key);
  }
  return { one: climbed.slice(0, cap), rest: climbed.slice(cap), touched, relit: lit };
}

/** `shown` with the named words' buildings as `from` has them: the town on screen, changed where it was earned. */
export function patchTown(shown: OwnTown, from: OwnTown, keys: readonly string[]): OwnTown {
  const want = new Set(keys), src = new Map(from.buildings.map((b) => [b.key, b]));
  return { ...shown, buildings: shown.buildings.map((b) => (want.has(b.key) && src.has(b.key) ? src.get(b.key)! : b)) };
}

/**
 * Folds one segment's reached keys into the sitting's accumulated set (v1
 * ticket 15 review): the beta gate can split one round into several segments,
 * `continueReview()` replacing the queue for each, but "what changed" (and
 * Stop here) must see every earlier segment too, not only the one on screen —
 * the bricks banked already keep growing the same way. A payoff already
 * played (another pass at today's round) starts a fresh sitting, its reached
 * keys included, just as it starts a fresh bank of bricks.
 */
export function foldSegment(acc: ReadonlySet<string>, builtAlready: boolean, segmentKeys: readonly string[]): Set<string> {
  return builtAlready ? new Set() : new Set([...acc, ...segmentKeys]);
}
